import React from 'react';
import { Link } from 'react-router-dom';

export type TrackingOwnerType = 'ORDER' | 'RETURN';

export interface MerchantTrackingLocation {
  trackingNumber: string;
  ownerType: TrackingOwnerType;
  ownerId: string;
}

export interface TrackingLinkProps extends MerchantTrackingLocation {
  children?: React.ReactNode;
  className?: string;
  title?: string;
  'aria-label'?: string;
}

export interface TrackingReference {
  trackingNumber?: string | null;
}

export const getUniqueTrackingNumbers = (
  trackings?: ReadonlyArray<TrackingReference | null | undefined> | null,
): string[] =>
  Array.from(
    new Set(
      (trackings ?? [])
        .map((tracking) => tracking?.trackingNumber?.trim())
        .filter((trackingNumber): trackingNumber is string =>
          Boolean(trackingNumber),
        ),
    ),
  );

export const buildMerchantTrackingPath = ({
  trackingNumber,
  ownerType,
  ownerId,
}: MerchantTrackingLocation): string | null => {
  const normalizedTrackingNumber = trackingNumber?.trim();
  const normalizedOwnerId = ownerId?.trim();

  if (
    !normalizedTrackingNumber ||
    !normalizedOwnerId ||
    !['ORDER', 'RETURN'].includes(ownerType)
  ) {
    return null;
  }

  const search = new URLSearchParams({
    ownerType,
    ownerId: normalizedOwnerId,
  });

  return `/tracking/${encodeURIComponent(
    normalizedTrackingNumber,
  )}?${search.toString()}`;
};

export const TrackingLink = ({
  trackingNumber,
  ownerType,
  ownerId,
  children,
  className,
  title,
  'aria-label': ariaLabel,
}: TrackingLinkProps) => {
  const content = children ?? trackingNumber;
  const path = buildMerchantTrackingPath({
    trackingNumber,
    ownerType,
    ownerId,
  });

  if (!path) {
    return (
      <span className={className} title={title} aria-label={ariaLabel}>
        {content}
      </span>
    );
  }

  return (
    <Link
      to={path}
      className={className ?? 'text-blue-600 underline hover:text-blue-700'}
      title={title}
      aria-label={ariaLabel}
    >
      {content}
    </Link>
  );
};
